import React from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Stack,
  Slide,
  useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { Box, keyframes } from "@mui/system";
import { useAppConfig } from "../../security/ApplicationProvider/ApplicationProvider";

// ─── Animations ──────────────────────────────────────────────────────────────

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const spinReverse = keyframes`
  to { transform: rotate(-360deg); }
`;

const pulse = keyframes`
  0%   { transform: scale(0.85); opacity: 0.5; }
  50%  { transform: scale(1.15); opacity: 0.1; }
  100% { transform: scale(0.85); opacity: 0.5; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-5px); }
`;

const dot = keyframes`
  0%, 80%, 100% { opacity: 0.2; transform: translateY(0); }
  40%           { opacity: 1; transform: translateY(-2px); }
`;

const sheen = keyframes`
  0%   { transform: translateX(-120%); }
  100% { transform: translateX(120%); }
`;

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export type TLoadingDialogType = "LOGO" | "DEFAULT";

interface LoadingDialogProps {
  open: boolean;
  /**
   * - "DEFAULT" (default): full card with white logo chip, text and background.
   * - "LOGO": just the animated logo + ring, no dialog background/card/text.
   */
  type?: TLoadingDialogType;
  message?: string;
  subMessage?: string;
}

const LoadingDialog: React.FC<LoadingDialogProps> = ({
  open,
  type = "DEFAULT",
  message = "Hold on! We're processing your data",
  subMessage = "This will only take a moment.",
}) => {
  const theme = useTheme();
  const { logo } = useAppConfig();
  const logoSrc = logo || "/signLogo.png";

  const primary = theme.palette.primary.main;
  const accent = theme.palette.warning.main;
  const isLogoOnly = type === "LOGO";

  // The animated logo + ring stage, shared by both variants.
  const logoStage = (
    <Box
      sx={{
        position: "relative",
        width: 150,
        height: 150,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Breathing halo */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${primary}2e 0%, transparent 68%)`,
          animation: `${pulse} 2.4s ease-in-out infinite`,
        }}
      />
      {/* Outer rotating arc */}
      <Box
        sx={{
          position: "absolute",
          inset: 8,
          borderRadius: "50%",
          background: `conic-gradient(from 0deg, transparent 0deg, transparent 250deg, ${primary} 320deg, ${accent} 360deg)`,
          WebkitMask:
            "radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))",
          mask: "radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))",
          animation: `${spin} 1.2s linear infinite`,
        }}
      />
      {/* Inner counter-rotating thin arc */}
      <Box
        sx={{
          position: "absolute",
          inset: 20,
          borderRadius: "50%",
          background: `conic-gradient(from 180deg, transparent 0deg, transparent 300deg, ${accent} 350deg, ${primary} 360deg)`,
          WebkitMask:
            "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px))",
          mask: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px))",
          animation: `${spinReverse} 1.8s linear infinite`,
          opacity: 0.7,
        }}
      />
      {/* Logo chip — white circle behind the logo in both variants */}
      <Box
        sx={{
          width: 104,
          height: 104,
          borderRadius: "50%",
          bgcolor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 6px 22px rgba(13,59,102,0.18)",
          animation: `${float} 2.6s ease-in-out infinite`,
          overflow: "hidden",
          zIndex: 1,
          p: 1.5,
        }}
      >
        <Box
          component="img"
          src={logoSrc}
          alt="Loading"
          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </Box>
    </Box>
  );

  // ─── LOGO variant: just the logo stage, no dialog card/background ────────────
  if (isLogoOnly) {
    return (
      <Dialog
        open={open}
        aria-labelledby="loading-dialog-title"
        TransitionComponent={Transition}
        transitionDuration={340}
        sx={{ zIndex: (t) => t.zIndex.modal + 10 }}
        BackdropProps={{ sx: { backgroundColor: "rgba(15,23,42,0.35)" } }}
        PaperProps={{
          elevation: 0,
          sx: {
            background: "transparent",
            boxShadow: "none",
            overflow: "visible",
            m: 0,
          },
        }}
      >
        {logoStage}
      </Dialog>
    );
  }

  // ─── DEFAULT variant: full card ──────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      aria-labelledby="loading-dialog-title"
      TransitionComponent={Transition}
      transitionDuration={340}
      sx={{ zIndex: (t) => t.zIndex.modal + 10 }}
      PaperProps={{
        sx: {
          width: { xs: "92vw", sm: 560 },
          maxWidth: 560,
          borderRadius: "24px",
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 30px 80px rgba(13, 59, 102, 0.30)",
        },
      }}
    >
      {/* Animated gradient top band with moving sheen */}
      <Box
        sx={{
          position: "relative",
          height: 6,
          background: `linear-gradient(90deg, ${primary}, ${accent}, ${primary})`,
          backgroundSize: "200% 100%",
          animation: `${gradientShift} 3s ease infinite`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            width: "40%",
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)",
            animation: `${sheen} 1.6s ease-in-out infinite`,
          }}
        />
      </Box>

      <DialogContent sx={{ px: { xs: 4, sm: 6 }, py: { xs: 4, sm: 5 } }}>
        <Stack direction="column" alignItems="center" spacing={3}>
          {logoStage}

          {/* Message */}
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
              }}
            >
              <Typography
                fontSize={{ xs: "1.15rem", sm: "1.35rem" }}
                fontWeight={700}
                sx={{ color: "#1E293B" }}
              >
                {message}
              </Typography>
              <Box sx={{ display: "inline-flex", ml: 0.5, mb: "6px" }}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    component="span"
                    sx={{
                      width: 4,
                      height: 4,
                      mx: "1.5px",
                      borderRadius: "50%",
                      bgcolor: primary,
                      animation: `${dot} 1.2s infinite ease-in-out`,
                      animationDelay: `${i * 0.2}s`,
                    }}
                  />
                ))}
              </Box>
            </Box>
            {subMessage && (
              <Typography
                fontSize={{ xs: "0.85rem", sm: "0.9rem" }}
                sx={{ color: "#64748B", mt: 0.75 }}
              >
                {subMessage}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default LoadingDialog;
